import { AnimatePresence, motion } from 'framer-motion';
import { Settings, Terminal, Zap } from 'lucide-react';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';

import { MANAGE_COMMANDS_ACTION_ID } from './constants';
import { SlashCommandSuggestionItem } from './types';

interface SlashCommandSuggestionListProps {
  items: SlashCommandSuggestionItem[];
  command: (item: SlashCommandSuggestionItem) => void;
  onManageCommands: () => void;
}

interface SlashCommandSuggestionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

export const SlashCommandSuggestionList = forwardRef<
  SlashCommandSuggestionListRef,
  SlashCommandSuggestionListProps
>((props, ref) => {
  const totalItems = props.items.length + 1; // +1 for "Manage Commands"
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectItem = (index: number) => {
    if (index === props.items.length) {
      props.onManageCommands();
      return;
    }
    const item = props.items[index];
    if (item) {
      props.command(item);
    }
  };

  useEffect(() => {
    setSelectedIndex(0);
  }, [props.items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSelectedIndex(
          (current) => (current + totalItems - 1) % totalItems,
        );
        return true;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSelectedIndex((current) => (current + 1) % totalItems);
        return true;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        selectItem(selectedIndex);
        return true;
      }

      return false;
    },
  }));

  const hasCommands = props.items.length > 0;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 4, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 4, scale: 0.98 }}
        transition={{ duration: 0.15, ease: 'easeOut' }}
      >
        <Command className="rounded-md bg-white border border-gray-200 overflow-hidden min-w-64 max-w-sm">
          <CommandList className="max-h-72 overflow-y-auto py-0">
            {/* Header */}
            <div className="px-3 py-1.5 border-b border-gray-100">
              <span className="text-xs font-medium text-gray-600">
                Slash Commands
              </span>
            </div>

            {hasCommands ? (
              <CommandGroup className="p-1">
                {props.items.map((item, index) => (
                  <CommandItem
                    key={item.id}
                    value={item.id}
                    onSelect={() => selectItem(index)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={cn(
                      'cursor-pointer transition-colors font-medium text-sm p-2 flex items-center gap-2.5 rounded',
                      index === selectedIndex && 'bg-primary/5',
                    )}
                  >
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-primary/10">
                      <Zap className="h-3.5 w-3.5 text-primary" />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="font-medium text-gray-900 font-mono text-xs leading-snug">
                        /{item.name}
                      </span>
                      {item.description && (
                        <span className="text-xs text-gray-600 truncate leading-snug">
                          {item.description}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : (
              <div className="flex flex-col items-center gap-1.5 px-4 py-4">
                <Terminal className="h-5 w-5 text-gray-600" />
                <p className="text-xs font-medium text-gray-600 text-center">
                  No commands yet
                </p>
                <p className="text-xs text-gray-600 text-center">
                  Click below to create your first
                </p>
              </div>
            )}

            <CommandSeparator />

            {/* Manage Commands action */}
            <CommandGroup className="p-1">
              <CommandItem
                key={MANAGE_COMMANDS_ACTION_ID}
                value={MANAGE_COMMANDS_ACTION_ID}
                onSelect={() => props.onManageCommands()}
                onMouseEnter={() => setSelectedIndex(props.items.length)}
                className={cn(
                  'cursor-pointer transition-colors font-medium text-sm p-2 flex items-center gap-2.5 rounded',
                  selectedIndex === props.items.length && 'bg-primary/5',
                )}
              >
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-gray-100">
                  <Settings className="h-3.5 w-3.5 text-gray-600" />
                </div>
                <span className="text-sm font-medium text-gray-600">
                  Manage Commands...
                </span>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </motion.div>
    </AnimatePresence>
  );
});

SlashCommandSuggestionList.displayName = 'SlashCommandSuggestionList';
